import type { JSX } from 'react';
import { useState } from 'react';
import { App, Input, Select } from 'antd';
import { ArrowRightOutlined, CameraOutlined, CheckOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';

import { educationApi, type QuizAnswerInput, type QuizClientQuestion } from '../../lib/wikiApi';

/**
 * Ввод ответа на вопрос теста (фаза 2.2 → HUB 2.0): все 8 типов, крупные
 * цели касания для телефона (варианты от 52 px, стрелки порядка 40 px),
 * фото-ответ открывает камеру (`capture`). Логика ответов прежняя —
 * формат QuizAnswerInput не менялся (проверка на бэке gradeQuestion).
 */
export function QuestionInput({
  q,
  attemptId,
  answer,
  onChange,
}: {
  q: QuizClientQuestion;
  attemptId: string;
  answer: QuizAnswerInput | undefined;
  onChange: (a: QuizAnswerInput) => void;
}): JSX.Element {
  const p = (q.payload ?? {}) as Record<string, unknown>;
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);

  if (q.kind === 'single' || q.kind === 'true_false' || q.kind === 'multi') {
    const multi = q.kind === 'multi';
    const sel = new Set(answer?.selectedOptionIds ?? []);
    const pick = (id: string): void => {
      if (!multi) {
        onChange({ questionId: q.id, selectedOptionIds: [id] });
        return;
      }
      const next = new Set(sel);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChange({ questionId: q.id, selectedOptionIds: Array.from(next) });
    };
    return (
      <div className="fk-learn-opts" role={multi ? 'group' : 'radiogroup'}>
        {multi && <div className="fk-learn-muted" style={{ fontSize: 12.5 }}>Можно выбрать несколько вариантов</div>}
        {q.options.map((o) => {
          const on = sel.has(o.id);
          return (
            <button key={o.id} type="button" className="fk-learn-opt" role={multi ? 'checkbox' : 'radio'} aria-checked={on} onClick={() => pick(o.id)}>
              <span className="fk-learn-mark" data-shape={multi ? 'check' : 'radio'}>{on ? <CheckOutlined /> : null}</span>
              <span>{o.text}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (q.kind === 'matching') {
    const left = Array.isArray(p.left) ? (p.left as string[]) : [];
    const right = Array.isArray(p.right) ? (p.right as string[]) : [];
    const value = Array.isArray(answer?.value) ? (answer!.value as Array<number | null>) : left.map(() => null);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {left.map((l, i) => (
          <div key={i} className="fk-learn-pair">
            <div className="fk-learn-pair-left">{l}</div>
            <ArrowRightOutlined style={{ color: 'var(--fk-text-muted)', justifySelf: 'center' }} />
            <Select
              size="large"
              value={value[i] ?? undefined}
              placeholder="Выберите…"
              onChange={(v) => {
                const next = [...value];
                next[i] = v;
                onChange({ questionId: q.id, value: next });
              }}
              options={right.map((r, j) => ({ value: j, label: r, disabled: value.some((v, k) => v === j && k !== i) }))}
              style={{ width: '100%', minWidth: 0 }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (q.kind === 'ordering') {
    const items = Array.isArray(p.items) ? (p.items as string[]) : [];
    const order = Array.isArray(answer?.value) && (answer!.value as number[]).length === items.length ? (answer!.value as number[]) : items.map((_, i) => i);
    const move = (from: number, dir: -1 | 1): void => {
      const to = from + dir;
      if (to < 0 || to >= order.length) return;
      const next = [...order];
      [next[from], next[to]] = [next[to], next[from]];
      onChange({ questionId: q.id, value: next });
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="fk-learn-muted" style={{ fontSize: 12.5 }}>Расставьте по порядку стрелками ↑ ↓</div>
        {order.map((shownIdx, pos) => (
          <div key={shownIdx} className="fk-learn-order">
            <span className="fk-learn-order-n">{pos + 1}</span>
            <span style={{ flex: 1, fontSize: 15 }}>{items[shownIdx]}</span>
            <button type="button" aria-label="Выше" disabled={pos === 0} onClick={() => move(pos, -1)}>↑</button>
            <button type="button" aria-label="Ниже" disabled={pos === order.length - 1} onClick={() => move(pos, 1)}>↓</button>
          </div>
        ))}
        {!answer && (
          <button type="button" className="fk-learn-chapter" style={{ alignSelf: 'flex-start', minHeight: 40 }} onClick={() => onChange({ questionId: q.id, value: order })}>
            <CheckOutlined /> Порядок верный — подтвердить
          </button>
        )}
      </div>
    );
  }

  if (q.kind === 'hotspot') {
    const v = answer?.value && typeof answer.value === 'object' ? (answer.value as { xPct: number; yPct: number }) : null;
    return (
      <div>
        <div
          style={{ position: 'relative', maxWidth: 720, cursor: 'crosshair', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--fk-border)' }}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            onChange({
              questionId: q.id,
              value: { xPct: Math.round(((e.clientX - r.left) / r.width) * 1000) / 10, yPct: Math.round(((e.clientY - r.top) / r.height) * 1000) / 10 },
            });
          }}
        >
          <img src={String(p.imageUrl ?? '')} alt="" style={{ width: '100%', display: 'block' }} draggable={false} />
          {v && (
            <div
              style={{
                position: 'absolute', left: `${v.xPct}%`, top: `${v.yPct}%`, width: 22, height: 22, marginLeft: -11, marginTop: -11,
                borderRadius: '50%', background: 'var(--fk-orange)', border: '3px solid #fff', boxShadow: '0 0 0 2px var(--fk-orange)', pointerEvents: 'none',
              }}
            />
          )}
        </div>
        <div className="fk-learn-muted" style={{ fontSize: 12.5, marginTop: 8 }}>Нажмите на нужное место на картинке. Отметку можно перенести повторным нажатием.</div>
      </div>
    );
  }

  if (q.kind === 'photo_upload') {
    const v = answer?.value && typeof answer.value === 'object' ? (answer.value as { attachmentId?: string; url?: string; fileName?: string; note?: string }) : {};
    const onFile = async (file: File): Promise<void> => {
      setUploading(true);
      try {
        const r = await educationApi.uploadAttemptPhoto(attemptId, file);
        onChange({ questionId: q.id, value: { ...v, attachmentId: r.id, url: r.url, fileName: r.fileName } });
      } catch (e) {
        message.error((e as Error).message);
      } finally {
        setUploading(false);
      }
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {p.hint ? <div className="fk-learn-muted" style={{ fontSize: 13 }}><InfoCircleOutlined /> {String(p.hint)}</div> : null}
        <label className="fk-learn-drop" data-filled={!!v.attachmentId} style={{ cursor: uploading ? 'wait' : 'pointer' }}>
          {v.url ? <img src={v.url} alt="" /> : uploading ? <LoadingOutlined style={{ fontSize: 26 }} /> : <CameraOutlined style={{ fontSize: 26 }} />}
          <span style={{ fontWeight: 700, color: 'var(--fk-text)' }}>{uploading ? 'Загружаем…' : v.attachmentId ? 'Фото прикреплено — заменить' : 'Сделать или выбрать фото'}</span>
          <span style={{ fontSize: 12 }}>С телефона откроется камера</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = '';
            }}
          />
        </label>
        <Input.TextArea
          value={v.note ?? ''}
          onChange={(e) => onChange({ questionId: q.id, value: { ...v, note: e.target.value } })}
          placeholder="Комментарий к фото (необязательно)"
          autoSize={{ minRows: 2, maxRows: 5 }}
          maxLength={2000}
        />
        <div className="fk-learn-muted" style={{ fontSize: 12 }}>Ответ проверяет руководитель вручную.</div>
      </div>
    );
  }

  // open
  return (
    <div>
      <Input.TextArea
        value={typeof answer?.value === 'string' ? answer.value : ''}
        onChange={(e) => onChange({ questionId: q.id, value: e.target.value })}
        placeholder="Ваш ответ…"
        autoSize={{ minRows: 4, maxRows: 10 }}
        maxLength={4000}
        style={{ fontSize: 15 }}
      />
      <div className="fk-learn-muted" style={{ fontSize: 12.5, marginTop: 8 }}>
        <InfoCircleOutlined /> {p.hint ? String(p.hint) : 'Ответ проверяет руководитель вручную.'}
      </div>
    </div>
  );
}
